import { useState, useEffect, useCallback } from 'react';

const STORAGE_PREFIX = 'planapus:firstView:';

/**
 * Hook to manage first-time expand behavior for collapsible sections.
 * - Auto-expands on first view
 * - Remembers collapsed state for subsequent views
 * - Cleans up old entries (older than 30 days)
 *
 * @param key - Unique identifier for the collapsible section (e.g., "step-info-{stepId}")
 * @param options - Configuration options
 * @returns [expanded, setExpanded, isFirstView]
 */
export function useFirstTimeExpand(
  key: string,
  options?: { defaultExpanded?: boolean }
): [boolean, (expanded: boolean) => void, boolean] {
  const storageKey = `${STORAGE_PREFIX}${key}`;
  const defaultExpanded = options?.defaultExpanded ?? true;

  // Check if this is the first view
  const [isFirstView, setIsFirstView] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null;
    } catch {
      return true;
    }
  });

  // Expanded state: first time defaults to expanded, otherwise check storage
  const [expanded, setExpandedState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) {
        // First view - auto-expand
        return defaultExpanded;
      }
      // Parse stored value
      const parsed = JSON.parse(stored);
      return parsed.expanded ?? !defaultExpanded;
    } catch {
      return defaultExpanded;
    }
  });

  // Persist state changes
  const setExpanded = useCallback((newExpanded: boolean) => {
    setExpandedState(newExpanded);
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        expanded: newExpanded,
        timestamp: Date.now(),
      }));
      setIsFirstView(false);
    } catch {
      // localStorage not available
    }
  }, [storageKey]);

  // Mark as visited on first render if first view
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isFirstView) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          expanded: expanded,
          timestamp: Date.now(),
        }));
      } catch {
        // localStorage not available
      }
    }
    // Only run once on mount - intentionally empty deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup old entries periodically
  useEffect(() => {
    try {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_PREFIX)) {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              const parsed = JSON.parse(value);
              if (parsed.timestamp && parsed.timestamp < thirtyDaysAgo) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Invalid entry, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      // localStorage not available
    }
  }, []);

  return [expanded, setExpanded, isFirstView];
}

export default useFirstTimeExpand;
